import { useState, useEffect } from 'react';
import { useCssData } from './hooks/useCssData';
import { BaselineByYear } from './components/BaselineByYear';

const OFFICIAL_SPECS = [
  {
    name: "CSS Level 2, latest revision",
    url: "https://www.w3.org/TR/CSS2/",
    code: "CSS2",
    description: "This defines the core of CSS, parts of which are overridden by later specifications. We recommend in particular reading Chapter 2, which introduces some of the basic concepts of CSS and its design principles."
  },
  {
    name: "CSS Syntax Module Level 3",
    url: "https://www.w3.org/TR/css-syntax-3/",
    code: "CSS-SYNTAX-3",
    description: "Replaces CSS2§4.1, CSS2§4.2, CSS2§4.4, and CSS2§G, redefining how CSS is parsed."
  },
  {
    name: "CSS Style Attributes",
    url: "https://www.w3.org/TR/css-style-attr/",
    code: "CSS-STYLE-ATTR",
    description: "Defines how CSS declarations can be embedded in markup attributes."
  },
  {
    name: "Media Queries Level 3",
    url: "https://www.w3.org/TR/css3-mediaqueries/",
    code: "CSS3-MEDIAQUERIES",
    description: "Replaces CSS2§7.3 and expands on the syntax for media-specific styles."
  },
  {
    name: "CSS Conditional Rules Module Level 3",
    url: "https://www.w3.org/TR/css-conditional-3/",
    code: "CSS-CONDITIONAL-3",
    description: "Extends and supersedes CSS2§7.2, updating the definition of ‘@media’ rules to allow nesting and introducing the ‘@supports’ rule for feature-support queries."
  },
  {
    name: "Selectors Level 3",
    url: "https://www.w3.org/TR/selectors-3/",
    code: "SELECTORS-3",
    description: "Replaces CSS2§5 and CSS2§6.4.3, defining an extended range of selectors."
  },
  {
    name: "CSS Namespaces Module Level 3",
    url: "https://www.w3.org/TR/css-namespaces/",
    code: "CSS3-NAMESPACE",
    description: "Introduces an ‘@namespace’ rule to allow namespace-prefixed selectors."
  },
  {
    name: "CSS Cascading and Inheritance Level 4",
    url: "https://www.w3.org/TR/css-cascade-4/",
    code: "CSS-CASCADE-4",
    description: "Extends and supersedes CSS2§1.4.3 and CSS2§6, as well as [CSS-CASCADE-3]. Describes how to collate style rules and assign values to all properties on all elements. By way of cascading and inheritance, values are propagated for all properties on all elements."
  },
  {
    name: "CSS Values and Units Module Level 3",
    url: "https://www.w3.org/TR/css-values-3/",
    code: "CSS-VALUES-3",
    description: "Extends and supersedes CSS2§1.4.2.1, CSS2§4.3, and CSS2§A.2.1–3, defining CSS’s property definition syntax and expanding its set of units."
  },
  {
    name: "CSS Custom Properties for Cascading Variables Module Level 1",
    url: "https://www.w3.org/TR/css-variables-1/",
    code: "CSS-VARIABLES-1",
    description: "Introduces cascading variables as a new primitive value type that is accepted by all CSS properties, and custom properties for defining them."
  },
  {
    name: "CSS Box Model Module Level 3",
    url: "https://www.w3.org/TR/css-box-3/",
    code: "CSS-BOX-3",
    description: "Replaces CSS2§8.1, §8.2, §8.3 (but not §8.3.1), and §8.4."
  },
  {
    name: "CSS Color Module Level 4",
    url: "https://www.w3.org/TR/css-color-4/",
    code: "CSS-COLOR-4",
    description: "Extends and supersedes CSS2§4.3.6, CSS2§14.1, and CSS2§18.2, also extends and supersedes [CSS-COLOR-3], introducing an extended range of color spaces beyond sRGB, extended color values, and CSS Object Model extensions for color. Also defines the ‘opacity’ property."
  },
  {
    name: "CSS Backgrounds and Borders Module Level 3",
    url: "https://www.w3.org/TR/css-backgrounds-3/",
    code: "CSS-BACKGROUNDS-3",
    description: "Extends and supersedes CSS2§8.5 and CSS2§14.2, providing more control of backgrounds and borders, including layered background images, image borders, and drop shadows."
  },
  {
    name: "CSS Images Module Level 3",
    url: "https://www.w3.org/TR/css-images-3/",
    code: "CSS-IMAGES-3",
    description: "Redefines and incorporates the external 2D image value type, introduces native 2D gradients, and adds additional controls for replaced element sizing and rendering."
  },
  {
    name: "CSS Fonts Module Level 3",
    url: "https://www.w3.org/TR/css-fonts-3/",
    code: "CSS-FONTS-3",
    description: "Extends and supersedes CSS2§15 and provides more control over font choice and feature selection."
  },
  {
    name: "CSS Writing Modes Level 3",
    url: "https://www.w3.org/TR/css-writing-modes-3/",
    code: "CSS-WRITING-MODES-3",
    description: "Defines CSS support for various international writing modes, such as left-to-right (e.g. Latin or Indic), right-to-left (e.g. Hebrew or Arabic), bidirectional (e.g. mixed Latin and Arabic) and vertical (e.g. Asian scripts). Replaces and extends CSS2§8.6 and §9.10."
  },
  {
    name: "CSS Multi-column Layout Module Level 1",
    url: "https://www.w3.org/TR/css-multicol-1/",
    code: "CSS-MULTICOL-1",
    description: "Introduces multi-column flows to CSS layout."
  },
  {
    name: "CSS Flexible Box Layout Module Level 1",
    url: "https://www.w3.org/TR/css-flexbox-1/",
    code: "CSS-FLEXBOX-1",
    description: "Introduces a flexible linear layout model for CSS."
  },
  {
    name: "CSS Basic User Interface Module Level 3",
    url: "https://www.w3.org/TR/css-ui-3/",
    code: "CSS-UI-3",
    description: "Extends and supersedes CSS2§18.1 and CSS2§18.4, defining ‘cursor’, ‘outline’, and several new CSS features that also enhance the user interface."
  },
  {
    name: "CSS Containment Module Level 1",
    url: "https://www.w3.org/TR/css-contain-1/",
    code: "CSS-CONTAIN-1",
    description: "Introduces the ‘contain’ property, which enforces the independent CSS processing of an element’s subtree in order to enable heavy optimizations by user agents when used well."
  },
  {
    name: "CSS Transforms Module Level 1",
    url: "https://www.w3.org/TR/css-transforms-1/",
    code: "CSS-TRANSFORMS-1",
    description: "Introduces coordinate-based graphical transformations to CSS."
  },
  {
    name: "Compositing and Blending Level 1",
    url: "https://www.w3.org/TR/compositing-1/",
    code: "COMPOSITING",
    description: "Defines the compositing and blending of overlaid content and introduces features to control their modes."
  },
  {
    name: "CSS Easing Functions Level 1",
    url: "https://www.w3.org/TR/css-easing-1/",
    code: "CSS-EASING-1",
    description: "Describes a way for authors to define a transformation that controls the rate of change of some value. Applied to animations, such transformations can be used to produce animations that mimic physical phenomena such as momentum or to cause the animation to move in discrete steps producing robot-like movement."
  },
  {
    name: "CSS Counter Styles Level 3",
    url: "https://www.w3.org/TR/css-counter-styles-3/",
    code: "CSS-COUNTER-STYLES-3",
    description: "Introduces the ‘@counter-style’ rule, which allows authors to define their own custom counter styles for use with CSS list-marker and generated-content counters [CSS-LISTS-3]. It also predefines a set of common counter styles, including the ones present in CSS2 and CSS2.1."
  }
];

function App() {
  const { data, loading, error } = useCssData();
  const [search, setSearch] = useState('');

  // Initialize view from hash if present
  const [view, setView] = useState(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
    if (hash === 'baseline-by-year' || hash === 'baseline') return 'baseline';
    return ['properties', 'browser-support', 'baseline', 'specs', 'definition'].includes(hash) ? hash : 'properties';
  });

  const [category, setCategory] = useState('all'); // 'all', 'property', 'value', 'function', 'at-rule'
  const [supportFilter, setSupportFilter] = useState('supported'); // 'supported', 'missing-any', 'missing-chrome', 'missing-firefox', 'missing-safari', 'full', 'all'
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationEnabled, setPaginationEnabled] = useState(true);
  const ITEMS_PER_PAGE = 9;

  // Handle hash changes (e.g. back/forward button)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash === 'baseline-by-year' || hash === 'baseline') {
        setView('baseline');
      } else if (['properties', 'browser-support', 'baseline', 'specs', 'definition'].includes(hash)) {
        setView(hash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  if (loading) return <div className="p-4">Loading CSS data...</div>;
  if (error) return <div className="p-4 text-red-500">Error loading data: {error.message}</div>;

  const allItems = [
      ...data.properties,
      ...data.atRules,
      ...data.values
  ];

  // Filter for Properties View
  const filteredItems = allItems.filter(item => {
    if (view !== 'properties') return false;
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = category === 'all' || item.type === category;
    return matchesSearch && matchesCategory;
  });

  // Filter for Browser Support View
  const supportedItems = allItems.filter(item => {
    if (view !== 'browser-support') return false;

    const compat = item.compatibility;
    const support = compat?.support || {};
    const isSupported = Boolean(compat?.supported);
    const hasChrome = Boolean(support.chrome);
    const hasFirefox = Boolean(support.firefox);
    const hasSafari = Boolean(support.safari);
    const isMissingAny = !hasChrome || !hasFirefox || !hasSafari;

    if (supportFilter === 'supported' && !isSupported) return false;
    if (supportFilter === 'missing-any' && !isMissingAny) return false;
    if (supportFilter === 'missing-chrome' && hasChrome) return false;
    if (supportFilter === 'missing-firefox' && hasFirefox) return false;
    if (supportFilter === 'missing-safari' && hasSafari) return false;
    if (supportFilter === 'full' && (isMissingAny || !isSupported)) return false;

    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = category === 'all' || item.type === category;
    return matchesSearch && matchesCategory;
  });

  const itemsToDisplay = view === 'properties' ? filteredItems : supportedItems;
  const totalPages = Math.ceil(itemsToDisplay.length / ITEMS_PER_PAGE);
  const currentItems = paginationEnabled
      ? itemsToDisplay.slice(
          (currentPage - 1) * ITEMS_PER_PAGE,
          currentPage * ITEMS_PER_PAGE
      )
      : itemsToDisplay;

  const handleSearchChange = (e) => {
      setSearch(e.target.value);
      setCurrentPage(1);
  };

  const handleViewChange = (newView) => {
      setView(newView);
      window.location.hash = newView;
      setSearch('');
      setCurrentPage(1);
  };

  const handleCategoryChange = (newCategory) => {
      setCategory(newCategory);
      setCurrentPage(1);
  };

  const handleSupportFilterChange = (newFilter) => {
      setSupportFilter(newFilter);
      setCurrentPage(1);
  };

  const completedSpecs = data.specsData.results.filter(spec =>
      spec.release &&
      spec.release.status === 'Recommendation' &&
      spec.groups &&
      spec.groups.some(g => g.name === 'Cascading Style Sheets (CSS) Working Group')
  );

  return (
    <div className="container mx-auto p-4">
      {/* Skip to Main Content Link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-700 focus:text-white focus:rounded-md focus:shadow-lg focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 font-semibold"
      >
        Skip to main content
      </a>

      {/* Header and Main Navigation */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">CSS Reference</h1>
          <p className="text-sm text-gray-500 mt-0.5">Explore properties, baseline availability by year, and specification standards</p>
        </div>
        <nav aria-label="Main Navigation" className="flex flex-wrap gap-2">
            <button
                className={`px-4 py-2 rounded font-medium transition-colors ${view === 'properties' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                onClick={() => handleViewChange('properties')}
                aria-current={view === 'properties' ? 'page' : undefined}
            >
                Properties
            </button>
            <button
                className={`px-4 py-2 rounded font-medium transition-colors ${view === 'browser-support' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                onClick={() => handleViewChange('browser-support')}
                aria-current={view === 'browser-support' ? 'page' : undefined}
            >
                Browser Support
            </button>
            <button
                className={`px-4 py-2 rounded font-medium transition-colors flex items-center gap-1.5 ${view === 'baseline' ? 'bg-blue-600 text-white shadow-xs' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                onClick={() => handleViewChange('baseline')}
                aria-current={view === 'baseline' ? 'page' : undefined}
            >
                <span>Baseline by Year</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${view === 'baseline' ? 'bg-blue-800 text-white' : 'bg-blue-100 text-blue-800 font-bold'}`}>
                  New
                </span>
            </button>
            <button
                className={`px-4 py-2 rounded font-medium transition-colors ${view === 'specs' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                onClick={() => handleViewChange('specs')}
                aria-current={view === 'specs' ? 'page' : undefined}
            >
                Completed Specs
            </button>
            <button
                className={`px-4 py-2 rounded font-medium transition-colors ${view === 'definition' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                onClick={() => handleViewChange('definition')}
                aria-current={view === 'definition' ? 'page' : undefined}
            >
                Official Definition
            </button>
        </nav>
      </header>

      {/* Main Landmark */}
      <main id="main-content" tabIndex="-1" className="focus:outline-hidden">
        {view === 'properties' && (
          <>
              {/* Screen Reader Live Region */}
              <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                {filteredItems.length} properties found
              </div>

              <section aria-labelledby="definitions-heading" className="bg-gray-50 border border-gray-200 rounded p-4 mb-6 text-sm text-gray-700">
                  <h2 id="definitions-heading" className="font-semibold mb-2 text-gray-900">CSS Definitions:</h2>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                          <span className="inline-block px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs font-bold mb-1">Property</span>
                          <p>A specific stylistic feature that you can modify (e.g., <code>color</code>, <code>font-size</code>).</p>
                      </div>
                      <div>
                          <span className="inline-block px-2 py-1 rounded bg-purple-100 text-purple-800 text-xs font-bold mb-1">Value</span>
                          <p>The setting or parameter applied to a property (e.g., <code>red</code>, <code>10px</code>, <code>bold</code>).</p>
                      </div>
                      <div>
                          <span className="inline-block px-2 py-1 rounded bg-pink-100 text-pink-800 text-xs font-bold mb-1">Function</span>
                          <p>A value that takes arguments to compute a result (e.g., <code>rgb()</code>, <code>calc()</code>).</p>
                      </div>
                      <div>
                          <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-bold mb-1">At-Rule</span>
                          <p>A statement that instructs CSS how to behave (e.g., <code>@media</code>, <code>@keyframes</code>).</p>
                      </div>
                  </div>
              </section>

              <section aria-label="Properties search and filters" className="flex flex-col md:flex-row gap-4 mb-4">
                  <div className="flex-grow">
                      <label htmlFor="search-props" className="sr-only">Search CSS properties</label>
                      <input
                          id="search-props"
                          type="text"
                          placeholder="Search properties..."
                          className="w-full p-2 border rounded"
                          value={search}
                          onChange={handleSearchChange}
                      />
                  </div>
                  <div>
                      <label htmlFor="category-props" className="sr-only">Filter by category</label>
                      <select
                          id="category-props"
                          value={category}
                          onChange={(e) => handleCategoryChange(e.target.value)}
                          className="p-2 border rounded bg-white w-full md:w-auto"
                      >
                          <option value="all">All Categories</option>
                          <option value="property">Properties</option>
                          <option value="value">Values</option>
                          <option value="function">Functions</option>
                          <option value="at-rule">At-Rules</option>
                      </select>
                  </div>
                  <div className="flex items-center space-x-2 bg-white p-2 border rounded shrink-0">
                      <input
                          type="checkbox"
                          id="pagination-toggle-props"
                          checked={paginationEnabled}
                          onChange={(e) => setPaginationEnabled(e.target.checked)}
                          className="h-4 w-4 text-blue-600 rounded"
                      />
                      <label htmlFor="pagination-toggle-props" className="text-sm text-gray-700 select-none cursor-pointer">
                          Pagination
                      </label>
                  </div>
              </section>

              <section aria-label="CSS Properties List" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {currentItems.map((item, index) => (
                  <article key={`${item.spec}-${item.name}-${index}`} aria-labelledby={`prop-${item.spec}-${item.name}-${index}`} className="border p-4 rounded shadow-sm hover:shadow-md transition relative bg-white flex flex-col justify-between">
                      <div>
                          <span className={`absolute top-2 right-2 text-xs px-2 py-1 rounded font-semibold ${
                              item.type === 'property' ? 'bg-blue-100 text-blue-800' :
                              item.type === 'at-rule' ? 'bg-green-100 text-green-800' :
                              item.type === 'function' ? 'bg-pink-100 text-pink-800' :
                              'bg-purple-100 text-purple-800'
                          }`}>
                              {item.type}
                          </span>
                          <h2 id={`prop-${item.spec}-${item.name}-${index}`} className="text-xl font-semibold text-blue-600 pr-16 font-mono">{item.name}</h2>
                          {item.sourceSpec && (
                              <p className="text-sm text-gray-500 mb-2">
                                  Defined in: <a href={item.sourceSpec.url} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600">{item.sourceSpec.title}<span className="sr-only"> (opens in new tab)</span></a>
                              </p>
                          )}
                          {item.note && (
                              <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded mb-2 border border-amber-200">
                                  <strong>Note:</strong> {item.note}
                              </p>
                          )}
                          {item.value && (
                              <p className="text-sm font-mono bg-gray-100 p-1.5 rounded truncate mb-2" title={item.value}>
                                  {item.value}
                              </p>
                          )}
                      </div>

                      <div>
                          {item.compatibility && item.compatibility.flagged && (
                              <p className="text-xs text-orange-600 bg-orange-50 p-1 rounded mt-2 border border-orange-200">
                                  ⚠️ Implemented behind a flag
                              </p>
                          )}
                          {item.href ? (
                              <a
                                  href={item.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:underline text-sm mt-3 inline-flex items-center gap-0.5"
                              >
                                  <span>View in Spec</span>
                                  <span aria-hidden="true">↗</span>
                                  <span className="sr-only"> (opens in new tab)</span>
                              </a>
                          ) : (
                              <span className="text-gray-400 text-sm mt-3 block cursor-not-allowed" title="Link not available">
                                  View in Spec (Not Available)
                              </span>
                          )}
                      </div>
                  </article>
                  ))}
              </section>

              {filteredItems.length === 0 && (
                  <p className="text-center text-gray-500 mt-8 py-8 bg-gray-50 rounded">No items found matching "{search}"</p>
              )}

              {filteredItems.length > 0 && paginationEnabled && (
                  <nav aria-label="Properties Pagination" className="flex justify-center items-center space-x-4 mt-8">
                      <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          aria-label="Go to previous page"
                          className="px-4 py-2 bg-gray-200 text-gray-800 rounded disabled:opacity-50 hover:bg-gray-300 transition-colors font-medium text-sm"
                      >
                          Previous
                      </button>
                      <span className="text-gray-700 text-sm" aria-current="page">
                          Page {currentPage} of {totalPages}
                      </span>
                      <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          aria-label="Go to next page"
                          className="px-4 py-2 bg-gray-200 text-gray-800 rounded disabled:opacity-50 hover:bg-gray-300 transition-colors font-medium text-sm"
                      >
                          Next
                      </button>
                  </nav>
              )}
          </>
        )}

        {view === 'browser-support' && (
          <>
              {/* Screen Reader Live Region */}
              <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                {itemsToDisplay.length} features found for current browser support filters
              </div>

              <section aria-labelledby="browser-support-heading" className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-lg p-4 mb-4">
                <h2 id="browser-support-heading" className="text-2xl font-bold text-gray-900 mb-1">Browser Support &amp; Compatibility Gaps</h2>
                <p className="text-sm text-gray-700 mb-2">
                  Analyze browser support and find interoperability gaps across major browser engines (Chrome, Firefox, Safari).
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                  <span>Total features matching filter: <strong className="text-gray-900">{itemsToDisplay.length}</strong></span>
                  {supportFilter === 'missing-any' && (
                    <span className="text-amber-800 font-semibold">⚠️ Showing features missing support in at least one major browser</span>
                  )}
                  {supportFilter === 'missing-chrome' && (
                    <span className="text-amber-800 font-semibold">⚠️ Showing features missing support in Chrome</span>
                  )}
                  {supportFilter === 'missing-firefox' && (
                    <span className="text-amber-800 font-semibold">⚠️ Showing features missing support in Firefox</span>
                  )}
                  {supportFilter === 'missing-safari' && (
                    <span className="text-amber-800 font-semibold">⚠️ Showing features missing support in Safari</span>
                  )}
                </div>
              </section>

              <section aria-label="Browser support search and filters" className="flex flex-col md:flex-row gap-3 mb-4 items-stretch md:items-center">
                  <div className="flex-grow">
                      <label htmlFor="search-support" className="sr-only">Search features</label>
                      <input
                          id="search-support"
                          type="text"
                          placeholder="Search features (e.g. subgrid, anchor, container)..."
                          className="w-full p-2 text-sm border rounded bg-white"
                          value={search}
                          onChange={handleSearchChange}
                      />
                  </div>

                  <div>
                      <label htmlFor="support-filter" className="sr-only">Filter by browser support status</label>
                      <select
                          id="support-filter"
                          value={supportFilter}
                          onChange={(e) => handleSupportFilterChange(e.target.value)}
                          className="w-full md:w-auto p-2 text-sm border rounded bg-white font-medium text-gray-700"
                      >
                          <option value="supported">Supported (≥2 browsers)</option>
                          <option value="missing-any">⚠️ Missing in 1+ browsers</option>
                          <option value="missing-chrome">Missing in Chrome</option>
                          <option value="missing-firefox">Missing in Firefox</option>
                          <option value="missing-safari">Missing in Safari</option>
                          <option value="full">Full Support (All 3 browsers)</option>
                          <option value="all">All Features</option>
                      </select>
                  </div>

                  <div>
                      <label htmlFor="category-support" className="sr-only">Filter by category</label>
                      <select
                          id="category-support"
                          value={category}
                          onChange={(e) => handleCategoryChange(e.target.value)}
                          className="w-full md:w-auto p-2 text-sm border rounded bg-white text-gray-700"
                      >
                          <option value="all">All Categories</option>
                          <option value="property">Properties</option>
                          <option value="value">Values</option>
                          <option value="function">Functions</option>
                          <option value="at-rule">At-Rules</option>
                      </select>
                  </div>

                  <div className="flex items-center space-x-2 bg-white p-2 border rounded shrink-0">
                      <input
                          type="checkbox"
                          id="missing-flag-toggle"
                          checked={supportFilter === 'missing-any'}
                          onChange={(e) => handleSupportFilterChange(e.target.checked ? 'missing-any' : 'supported')}
                          className="h-4 w-4 text-amber-600 focus:ring-amber-500 rounded"
                      />
                      <label htmlFor="missing-flag-toggle" className="text-xs font-semibold text-gray-700 select-none cursor-pointer">
                          Missing in 1+ Browsers
                      </label>
                  </div>

                  <div className="flex items-center space-x-2 bg-white p-2 border rounded shrink-0">
                      <input
                          type="checkbox"
                          id="pagination-toggle-support"
                          checked={paginationEnabled}
                          onChange={(e) => setPaginationEnabled(e.target.checked)}
                          className="h-4 w-4 text-blue-600 rounded"
                      />
                      <label htmlFor="pagination-toggle-support" className="text-xs text-gray-700 select-none cursor-pointer">
                          Pagination
                      </label>
                  </div>
              </section>

              <section aria-label="Browser Supported Features List" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {currentItems.map((item, index) => {
                      const support = item.compatibility?.support || {};
                      const missingBrowsers = ['chrome', 'firefox', 'safari'].filter(b => !support[b]);

                      return (
                          <article key={`${item.spec}-${item.name}-${index}`} aria-labelledby={`supp-${item.spec}-${item.name}-${index}`} className="border p-4 rounded shadow-sm hover:shadow-md transition relative bg-white flex flex-col justify-between">
                              <div>
                                  <span className={`absolute top-2 right-2 text-xs px-2 py-1 rounded font-semibold ${
                                      item.type === 'property' ? 'bg-blue-100 text-blue-800' :
                                      item.type === 'at-rule' ? 'bg-green-100 text-green-800' :
                                      item.type === 'function' ? 'bg-pink-100 text-pink-800' :
                                      'bg-purple-100 text-purple-800'
                                  }`}>
                                      {item.type}
                                  </span>
                                  <h2 id={`supp-${item.spec}-${item.name}-${index}`} className="text-xl font-semibold text-blue-600 pr-16 font-mono">{item.name}</h2>
                                  {item.sourceSpec && (
                                      <p className="text-xs text-gray-500 mb-2">
                                          Defined in: <a href={item.sourceSpec.url} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600">{item.sourceSpec.title}<span className="sr-only"> (opens in new tab)</span></a>
                                      </p>
                                  )}

                                  {item.note && (
                                      <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded mb-2 border border-amber-200">
                                          <strong>Note:</strong> {item.note}
                                      </p>
                                  )}

                                  {item.value && (
                                      <p className="text-xs font-mono bg-gray-100 p-1.5 rounded truncate mb-2" title={item.value}>
                                          {item.value}
                                      </p>
                                  )}

                                  {missingBrowsers.length > 0 && (
                                      <div className="mb-2">
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                              <span>⚠️ Not supported in:</span>
                                              <span className="capitalize font-bold">{missingBrowsers.join(', ')}</span>
                                          </span>
                                      </div>
                                  )}
                              </div>

                              <div>
                                  <div className="mt-3 flex space-x-2" aria-label={`Browser compatibility for ${item.name}`}>
                                      {['chrome', 'firefox', 'safari'].map(browser => {
                                          const isSupported = support[browser];
                                          return (
                                              <div
                                                  key={browser}
                                                  className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-bold ${
                                                      isSupported ? 'bg-green-100 text-green-800' : 'bg-red-50 text-red-700 border border-red-200'
                                                  }`}
                                              >
                                                  <span className="capitalize">{browser}</span>
                                                  <span className="sr-only">: {isSupported ? 'Supported' : 'Not supported'}</span>
                                                  <span aria-hidden="true">{isSupported ? '✓' : '✗'}</span>
                                              </div>
                                          );
                                      })}
                                  </div>

                                  {item.compatibility?.flagged && (
                                      <p className="text-xs text-orange-600 bg-orange-50 p-1 rounded mt-2 border border-orange-200">
                                          ⚠️ Implemented behind a flag
                                      </p>
                                  )}

                                  {item.href ? (
                                      <a
                                          href={item.href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-500 hover:underline text-xs mt-3 inline-flex items-center gap-0.5"
                                      >
                                          <span>View in Spec</span>
                                          <span aria-hidden="true">↗</span>
                                          <span className="sr-only"> (opens in new tab)</span>
                                      </a>
                                  ) : (
                                      <span className="text-gray-400 text-xs mt-3 block cursor-not-allowed" title="Link not available">
                                          View in Spec (Not Available)
                                      </span>
                                  )}
                              </div>
                          </article>
                      );
                  })}
              </section>

              {itemsToDisplay.length === 0 && (
                  <p className="text-center text-gray-500 mt-8 py-8 bg-gray-50 rounded">No items found matching the current filters and "{search}"</p>
              )}

              {itemsToDisplay.length > 0 && paginationEnabled && (
                  <nav aria-label="Browser Support Pagination" className="flex justify-center items-center space-x-4 mt-8">
                      <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          aria-label="Go to previous page"
                          className="px-4 py-2 bg-gray-200 text-gray-800 rounded disabled:opacity-50 font-medium text-sm hover:bg-gray-300 transition-colors"
                      >
                          Previous
                      </button>
                      <span className="text-gray-700 text-sm" aria-current="page">
                          Page {currentPage} of {totalPages}
                      </span>
                      <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          aria-label="Go to next page"
                          className="px-4 py-2 bg-gray-200 text-gray-800 rounded disabled:opacity-50 font-medium text-sm hover:bg-gray-300 transition-colors"
                      >
                          Next
                      </button>
                  </nav>
              )}
          </>
        )}

        {view === 'specs' && (
            <div>
                <h2 className="text-2xl font-bold mb-4">Completed Specifications</h2>

                <p className="mb-4 text-gray-700">
                    The following specifications have reached "Recommendation" status, meaning they are considered completed.
                    Note that the search results above may show properties from newer drafts (e.g. Level 4) that extend these recommendations.
                </p>
                <p className="mb-4 text-gray-700">
                  This is naive starting point to understanding the current state of CSS. All specifications approved as recommendations by the W3C are ready to use an implement.
                </p>
                <p className="mb-4 text-gray-700">
                  However, the landscape of CSS is constantly evolving, with new drafts and proposals emerging regularly and features from editor drafts or candidate recommendations being implemented in browsers before specifications reach full recommendation status so this list is icomplete in terms of practical usage
                </p>
                <p className="mb-4 text-gray-700">
                  Even if the list was complete, asking developers to read each specification is not only unrealistic but also inefficient. Many specifications build upon or reference others, leading to redundancy and overlap. A more effective approach would be to provide curated guides or summaries that distill the essential information from these specifications, making it easier for developers to grasp the key concepts and best practices without wading through extensive documentation.
                </p>
                <p className="mb-4 text-gray-700">It is also worth noting that W3C specifications are not meant for developers to read but for browser implementers to use when adding features to their engines and for the CSS working group to track progress on standardization.</p>
                <section aria-label="Completed Specifications List" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {completedSpecs.map((spec, index) => (
                        <article key={index} className="border p-4 rounded shadow-sm bg-white hover:shadow-md transition">
                            <h3 className="text-xl font-semibold">{spec.title || spec.shortname}</h3>
                            <p className="text-sm text-gray-500 mb-2">{spec.release.status}</p>
                            <a
                                href={spec.release.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline inline-flex items-center gap-0.5 text-sm font-medium"
                            >
                                <span>Read Specification</span>
                                <span aria-hidden="true">↗</span>
                                <span className="sr-only"> (opens in new tab)</span>
                            </a>
                        </article>
                    ))}
                </section>
            </div>
        )}

        {view === 'definition' && (
            <div>
                <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">Cascading Style Sheets (CSS) — The Official Definition</h2>
                    <p className="mb-4 text-gray-800">
                        According to the <a href="https://www.w3.org/TR/css-2025/#css-official" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">CSS Snapshot 2025<span className="sr-only"> (opens in new tab)</span></a>:
                    </p>
                    <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-800 mb-4">
                        <p className="mb-2">
                            This profile includes only specifications that the CSS Working Group considers stable and for which they have enough implementation experience that they are sure of that stability.
                        </p>
                    </blockquote>
                      <p className="mb-4 text-gray-800">
                        However, any consideration of a future version of CSS should also include an analysis of the specification in the <a href="https://www.w3.org/TR/css-2026/#reliable-cr " target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">Reliable Candidate Recommendations<span className="sr-only"> (opens in new tab)</span></a> section. While these specifications are have not yet reached the recommendation stage, they have been implemented in multiple browsers and are likely to be included in the official defintion of CSS in the near
                      </p>
                    <p className="text-gray-800">
                        As of 2025, Cascading Style Sheets (CSS) is defined by the specifications listed in the official snapshot.
                    </p>
                </div>

                <section aria-label="Official Specification Profiles" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {OFFICIAL_SPECS.map((spec, index) => (
                        <article key={index} className="border p-4 rounded shadow-sm bg-white hover:shadow-md transition">
                            <h3 className="font-semibold text-lg mb-1">{spec.name}</h3>
                            <p className="text-sm text-gray-600 mb-3">{spec.description}</p>
                            <div className="flex justify-between items-center mt-2">
                                <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-600">
                                    {spec.code}
                                </span>
                                <a
                                    href={spec.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline text-sm font-medium inline-flex items-center gap-0.5"
                                >
                                    <span>Read Spec</span>
                                    <span aria-hidden="true">↗</span>
                                    <span className="sr-only"> (opens in new tab)</span>
                                </a>
                            </div>
                        </article>
                    ))}
                </section>
            </div>
        )}

        {view === 'baseline' && (
            <BaselineByYear baselineFeatures={data.baselineFeatures || []} />
        )}
      </main>
    </div>
  );
}

export default App;
